import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import type { Lesson, Subject } from '../api';
import { seriesOf } from '../fields';
import FieldInputs from '../FieldInputs';
import { FieldSeries } from './FieldSeries';

export type LessonDetails = { goal: string; materials: string };

type CellDetailsProps = {
  subject: Subject | undefined;
  lesson: Lesson | undefined;
  /** Saves goal and materials when a field is left; absent = read-only. */
  onDetails?: (details: LessonDetails) => void;
  /** Sets values of the subject's own fields; absent = read-only. */
  onFields?: (values: Record<string, string>) => void;
};

const DETAIL_FIELDS: { key: keyof LessonDetails; label: string }[] = [
  { key: 'goal', label: 'Doel' },
  { key: 'materials', label: 'Materiaal' },
];

/** The day plan of one lesson: goal, materials and the subject's own fields. Used in the cell popover and the day view. */
export function CellDetails({ subject, lesson, onDetails, onFields }: CellDetailsProps) {
  const saved: LessonDetails = { goal: lesson?.goal ?? '', materials: lesson?.materials ?? '' };
  const [draft, setDraft] = useState(saved);
  const commit = () => {
    if (onDetails && DETAIL_FIELDS.some(({ key }) => draft[key].trim() !== saved[key])) onDetails(draft);
  };
  const values = lesson?.fieldValues;
  const seriesFields = subject?.fields.filter((field) => seriesOf(field) !== null) ?? [];
  const otherFields = subject?.fields.filter((field) => seriesOf(field) === null) ?? [];

  return (
    <div className="flex flex-col gap-2">
      {seriesFields.map((field) => (
        <FieldSeries
          key={field.id}
          field={field}
          value={values?.[field.id] ?? ''}
          {...(onFields ? { onPick: (value: string) => onFields({ [field.id]: value }) } : {})}
        />
      ))}
      {subject && otherFields.length > 0 && onFields && (
        <FieldInputs subject={{ ...subject, fields: otherFields }} values={values} onChange={onFields} />
      )}
      {onDetails ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {DETAIL_FIELDS.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1 text-sm">
              {label}
              <Textarea
                value={draft[key]}
                maxLength={500}
                rows={2}
                onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
                onBlur={commit}
                className="auto-grow resize-none text-base"
              />
            </label>
          ))}
        </div>
      ) : (
        DETAIL_FIELDS.filter(({ key }) => saved[key]).map(({ key, label }) => (
          <p key={key} className="whitespace-pre-wrap">
            <span className="text-[0.75em] opacity-70">{label} </span>
            {saved[key]}
          </p>
        ))
      )}
    </div>
  );
}
